import { AutoComplete, Form } from 'antd';
import React, { useEffect, useRef, useState } from 'react';
import debounce from 'lodash.debounce';
import { useToken } from '@/common/helper';
import { GetSettingsLocationById } from '@/apis/settings';

interface Location {
  id?: string;
  name?: string;
  address?: string;
  latitude?: number | string;
  longitude?: number | string;
  polygon?: Array<{ lat: number; lng: number }>;
}

interface PlaceDetails {
  name: string;
  address: string;
  latitude: number;
  longitude: number;
  zip_code?: string;
  country?: string;
  id?: string;
  placeName?: string;
}

interface AutoCompleteOption {
  label: string;
  value: string;
  source: 'api' | 'google';
  keyId?: string;
}

/**
 * Dropdown-only rendering. Must NOT be put on option `label`: rc-select combobox
 * also paints `label` behind the input, so React rich labels cause double/ghost text.
 */
function formatDropdownLabel(option: AutoCompleteOption): React.ReactNode {
  // Google predictions: show one readable line (main_text bold looked like a false "name").
  if (option.source === 'google') {
    return (
      <span style={{ color: 'var(--text-secondary)' }}>{option.label}</span>
    );
  }

  const sep = ' · ';
  const dotIdx = option.label.indexOf(sep);
  if (dotIdx >= 0) {
    const namePart = option.label.slice(0, dotIdx);
    const addressPart = option.label.slice(dotIdx + sep.length);
    return (
      <>
        <span style={{ fontWeight: 700, color: 'var(--text-primary)' }}>
          {namePart}
        </span>
        <span style={{ color: 'var(--text-secondary)' }}>
          &nbsp;·&nbsp;{addressPart}
        </span>
      </>
    );
  }

  return (
    <span style={{ fontWeight: 700, color: 'var(--text-primary)' }}>
      {option.label}
    </span>
  );
}

/** Metadata passed through Ant Design option (`label` stays a plain string). */
interface SelectOptionMeta {
  keyId: string;
  source: 'api' | 'google';
  labelText: string;
}

/** Third-arg meta so parents can tell lightweight DDL commit from detail enrichment. */
export type AddressPlaceSelectOptions = {
  isDetailEnrichment?: boolean;
};

interface LocationAutoCompleteShipmentProps {
  locationList: Location[];
  handleAddressPlaceSelect: (
    location: Location | PlaceDetails,
    fieldName?: string,
    options?: AddressPlaceSelectOptions
  ) => void;
  fieldName: string;
  label: string;
  width?: string;
  className?: string;
  placeholder?: string;
  externalValue?: string;
  onInputChange?: (value: string) => void;
  isSaved?: boolean;
  onSavedClick?: () => void;
  suffixIcon?: React.ReactNode;
  /** Fires around GetSettingsLocationById so parents can gate geo-fence UI. */
  onDetailHydrationChange?: (
    fieldName: string,
    phase: 'start' | 'done'
  ) => void;
}

const LocationAutoCompleteShipment: React.FC<
  LocationAutoCompleteShipmentProps
> = ({
  locationList,
  handleAddressPlaceSelect,
  fieldName,
  label,
  width = '262px',
  className = '',
  placeholder = '',
  externalValue,
  onInputChange,
  isSaved = false,
  onSavedClick,
  suffixIcon,
  onDetailHydrationChange,
}) => {
  const { token } = useToken();
  const [options, setOptions] = useState<AutoCompleteOption[]>([]);
  const [inputValue, setInputValue] = useState<string>('');
  const autoCompleteRef = useRef<React.ComponentRef<
    typeof AutoComplete
  > | null>(null);
  const prevIsSavedRef = useRef(isSaved);
  const locationListRef = useRef(locationList);
  const searchGenRef = useRef(0);
  // Bumps on every select so a slow GetSettingsLocationById cannot overwrite
  // a newer selection with a stale enriched record.
  const selectGenRef = useRef(0);
  // Label committed with the in-flight detail request; used to detect parent
  // detach / controlled-value changes that never call handleSearch.
  const pendingSelectLabelRef = useRef<string | null>(null);
  // True only after the user types/searches; cleared on select/clear so a
  // prefilled edit-drawer address never triggers Google on DDL arrival.
  const activeSearchRef = useRef(false);

  locationListRef.current = locationList;

  // Stable debounced Google fetch — recreating debounce each render drops in-flight
  // work and lets stale callbacks overwrite newer results (e.g. Abcam from "a"
  // sticking after the user continues typing "mina").
  const fetchGooglePlacesRef = useRef(
    debounce(
      (
        input: string,
        prepend: AutoCompleteOption[],
        gen: number,
        isCurrent: () => boolean
      ) => {
        if (!input || !isCurrent()) return;

        const places = window.google?.maps?.places;
        if (!places?.AutocompleteService || !places.PlacesServiceStatus) {
          if (prepend.length === 0 && isCurrent()) setOptions([]);
          return;
        }

        const service = new places.AutocompleteService();
        service.getPlacePredictions({ input }, (predictions, status) => {
          if (!isCurrent()) return;

          if (
            status === places.PlacesServiceStatus.OK &&
            Array.isArray(predictions) &&
            predictions.length > 0
          ) {
            setOptions([
              ...prepend,
              ...predictions.map((prediction) => ({
                label: prediction?.description ?? '',
                value: prediction?.place_id ?? '',
                source: 'google' as const,
              })),
            ]);
            return;
          }

          // Google returned nothing / error — keep local matches, else clear
          // so prior unrelated rows (e.g. Abcam) cannot linger.
          setOptions(prepend);
        });
      },
      300
    )
  );

  useEffect(() => {
    const debounced = fetchGooglePlacesRef.current;
    return () => debounced.cancel();
  }, []);

  useEffect(() => {
    if (externalValue !== undefined) setInputValue(externalValue);
    // Detach (and some parent edits) change the controlled value without
    // going through handleSearch — drop in-flight detail hydration.
    if (
      pendingSelectLabelRef.current != null &&
      externalValue !== undefined &&
      externalValue !== pendingSelectLabelRef.current
    ) {
      selectGenRef.current += 1;
      pendingSelectLabelRef.current = null;
    }
  }, [externalValue]);

  // Saved ↔ editable transitions are parent-driven (select, Save Location modal,
  // edit/detach). Keep search/hydration gens aligned with that committed state.
  useEffect(() => {
    if (!prevIsSavedRef.current && isSaved) {
      // Parent committed a saved location — stop active search so a concurrent
      // locationList refresh (optimistic prepend / DDL reload) cannot reissue
      // Google Places for the old query.
      activeSearchRef.current = false;
      fetchGooglePlacesRef.current.cancel();
      searchGenRef.current += 1;
      setOptions([]);
    }
    if (prevIsSavedRef.current && !isSaved) {
      // Edit-only (label unchanged): keep in-flight GetSettingsLocationById so
      // lat/lng/polygon can still hydrate. Detach / typed edits cancel via the
      // externalValue effect or handleSearch instead.
      setTimeout(() => autoCompleteRef.current?.focus(), 0);
    }
    prevIsSavedRef.current = isSaved;
  }, [isSaved]);

  const fetchPlaceDetails = (placeId: string): Promise<PlaceDetails> => {
    return new Promise((resolve, reject) => {
      const service = new window.google.maps.places.PlacesService(
        document.createElement('div')
      );
      service.getDetails(
        {
          placeId,
          fields: [
            'formatted_address',
            'geometry',
            'name',
            'address_components',
          ],
        },
        (place, status) => {
          if (status === window.google.maps.places.PlacesServiceStatus.OK) {
            const components = place.address_components ?? [];
            const getComponent = (type: string) =>
              components.find((c) => c.types.includes(type))?.long_name ?? '';
            resolve({
              name: place.name,
              address: place.formatted_address,
              latitude: place.geometry.location.lat(),
              longitude: place.geometry.location.lng(),
              zip_code: getComponent('postal_code'),
              country: getComponent('country'),
            });
          } else {
            reject(status);
          }
        }
      );
    });
  };

  const handleSearch = (input: string) => {
    setInputValue(input);
    const gen = ++searchGenRef.current;
    const isCurrent = () => searchGenRef.current === gen;
    // Drop in-flight GetSettingsLocationById / Places detail enrichment so an
    // edit or clear after select cannot restore the previous location.
    selectGenRef.current += 1;
    pendingSelectLabelRef.current = null;

    if (!input) {
      activeSearchRef.current = false;
      setOptions([]);
      fetchGooglePlacesRef.current.cancel();
      return;
    }

    activeSearchRef.current = true;
    const lowerInput = input.toLowerCase();
    const localMatches: AutoCompleteOption[] = locationListRef.current
      .filter(
        (location) =>
          location.name?.toLowerCase().includes(lowerInput) ||
          location.address?.toLowerCase().includes(lowerInput)
      )
      .map((location) => ({
        label: location.address
          ? `${location.name} · ${location.address}`
          : (location.name ?? ''),
        value: String(location.id ?? ''),
        source: 'api' as const,
      }))
      .filter((option) => option.value && option.label);

    // Always replace options immediately so a prior query's rows cannot stick
    // while Google is in flight (empty local → clear, don't keep Abcam/etc.).
    setOptions(localMatches);
    // Drop any pending trailing fetch so an older query cannot run after a
    // newer clear (lodash keeps the latest args, but cancel is explicit).
    fetchGooglePlacesRef.current.cancel();
    fetchGooglePlacesRef.current(input, localMatches, gen, isCurrent);
  };

  // When the company list loads or refreshes while the user has an active
  // query, rebuild options so new/updated matches appear without another
  // keystroke. Gated on activeSearchRef (and not isSaved) so prefilled /
  // parent-committed saved rows never hit Google. Empty lists still flow
  // through so stale company rows are cleared. Never setInputValue here.
  useEffect(() => {
    if (!activeSearchRef.current || isSaved) return;

    const query = inputValue.trim();
    if (!query) return;

    const gen = ++searchGenRef.current;
    const isCurrent = () => searchGenRef.current === gen;
    const lowerInput = query.toLowerCase();
    const localMatches: AutoCompleteOption[] = locationList
      .filter(
        (location) =>
          location.name?.toLowerCase().includes(lowerInput) ||
          location.address?.toLowerCase().includes(lowerInput)
      )
      .map((location) => ({
        label: location.address
          ? `${location.name} · ${location.address}`
          : (location.name ?? ''),
        value: String(location.id ?? ''),
        source: 'api' as const,
      }))
      .filter((option) => option.value && option.label);

    setOptions(localMatches);
    fetchGooglePlacesRef.current.cancel();
    fetchGooglePlacesRef.current(query, localMatches, gen, isCurrent);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- when list changes during an active search
  }, [locationList]);

  const handleSelect = async (keyId: string, option: SelectOptionMeta) => {
    // Selection must not keep searching or leave option keys (api:/google:) in the
    // input — Create/Edit mirror onInputChange into externalValue.
    activeSearchRef.current = false;
    fetchGooglePlacesRef.current.cancel();
    searchGenRef.current += 1;
    const selectGen = ++selectGenRef.current;
    pendingSelectLabelRef.current = option.labelText;
    setInputValue(option.labelText);
    onInputChange?.(option.labelText);

    if (option.source === 'api') {
      const selectedLocation = locationList.find(
        (loc) => loc.id === option.keyId
      );
      if (selectedLocation) {
        // Commit the lightweight row immediately so Create/Edit can validate /
        // submit while dropdown-mode detail hydration is in flight.
        handleAddressPlaceSelect(selectedLocation, fieldName, {
          isDetailEnrichment: false,
        });

        // Dropdown-mode lists omit polygon/lat/lng; enrich so geo-fence is kept.
        if (selectedLocation.latitude === undefined && token) {
          onDetailHydrationChange?.(fieldName, 'start');
          try {
            const res = await GetSettingsLocationById(
              token,
              selectedLocation.id ?? option.keyId
            );
            if (selectGen !== selectGenRef.current) return;
            const full = res?.data?.data;
            if (full?.is_success && full.data) {
              pendingSelectLabelRef.current = null;
              handleAddressPlaceSelect(full.data, fieldName, {
                isDetailEnrichment: true,
              });
            }
          } catch {
            // Parent already has the lightweight record from the commit above.
          } finally {
            // Always settle for this generation so a null-coord location or a
            // failed detail fetch cannot leave the geo-fence editor disabled.
            if (selectGen === selectGenRef.current) {
              onDetailHydrationChange?.(fieldName, 'done');
            }
          }
        } else {
          pendingSelectLabelRef.current = null;
        }
      }
    } else if (option.source === 'google') {
      try {
        const placeDetails = await fetchPlaceDetails(keyId);
        if (selectGen !== selectGenRef.current) return;
        pendingSelectLabelRef.current = null;
        handleAddressPlaceSelect(
          { ...placeDetails, id: keyId, placeName: option.labelText },
          fieldName
        );
      } catch (error) {
        console.error('Failed to fetch place details: ', error);
      }
    }
  };

  const iconSlot = suffixIcon ? (
    <span
      style={{
        position: 'absolute',
        right: 10,
        top: '50%',
        transform: 'translateY(-50%)',
        display: 'flex',
        alignItems: 'center',
        zIndex: 1,
      }}
    >
      {suffixIcon}
    </span>
  ) : null;

  if (isSaved) {
    const dotIndex = inputValue?.indexOf(' · ') ?? -1;
    const namePart = dotIndex >= 0 ? inputValue.slice(0, dotIndex) : inputValue;
    const addressPart = dotIndex >= 0 ? inputValue.slice(dotIndex + 3) : null;
    return (
      <Form.Item
        label={label}
        name={fieldName}
        style={{ marginBottom: '16px', width: width }}
        className={className}
      >
        <div style={{ position: 'relative', height: '36px' }}>
          <div
            onClick={onSavedClick}
            style={{
              width: '100%',
              height: '36px',
              backgroundColor: 'var(--light-gray)',
              borderRadius: '8px',
              display: 'flex',
              alignItems: 'center',
              padding: suffixIcon ? '0 36px 0 11px' : '0 11px',
              cursor: 'text',
              overflow: 'hidden',
              whiteSpace: 'nowrap',
              fontSize: 14,
              color: 'var(--text-secondary)',
            }}
          >
            <span
              style={{
                fontWeight: 700,
                color: 'var(--text-primary)',
                flexShrink: 0,
              }}
            >
              {namePart}
            </span>
            {addressPart && (
              <span
                style={{
                  marginLeft: 4,
                  flexShrink: 1,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                &nbsp;·&nbsp;{addressPart}
              </span>
            )}
          </div>
          {iconSlot}
        </div>
      </Form.Item>
    );
  }

  return (
    <Form.Item
      label={label}
      name={fieldName}
      style={{ marginBottom: '16px', width: width }}
      className={className}
    >
      <div style={{ position: 'relative', height: '36px' }}>
        <AutoComplete
          ref={autoCompleteRef}
          className={suffixIcon ? 'autocomplete-with-suffix' : undefined}
          style={{
            width: '100%',
            boxShadow: 'none',
            height: '36px',
            backgroundColor: 'var(--light-gray)',
            color: 'var(--text-secondary)',
            borderRadius: '8px',
          }}
          getPopupContainer={(trigger) =>
            trigger.closest('.ant-drawer-body') ?? document.body
          }
          filterOption={false}
          optionRender={(item) =>
            formatDropdownLabel(item.data as AutoCompleteOption)
          }
          options={options.map((option) => ({
            label: option.label,
            // Unique value — duplicate labels (22× "Abcam Limited · …") break
            // rc-select when value === label.
            value: `${option.source}:${option.value}`,
            keyId: String(option.value),
            source: option.source,
            labelText: option.label,
          }))}
          value={inputValue}
          onSearch={handleSearch}
          onSelect={(_value, option) =>
            handleSelect(option.keyId as string, option as SelectOptionMeta)
          }
          onChange={(val) => {
            // AutoComplete also fires onChange on select with option.value
            // (api:<id> / google:<place_id>). Only treat exact current option
            // keys as selection — startsWith alone would swallow free text like
            // "api:warehouse" and leave parent address state stale.
            const selected = options.find(
              (o) => `${o.source}:${o.value}` === val
            );
            if (selected) {
              setInputValue(selected.label);
              onInputChange?.(selected.label);
              fetchGooglePlacesRef.current.cancel();
              searchGenRef.current += 1;
              return;
            }
            // Keep options in sync on paste/clear; onSearch alone can miss some
            // input paths and leave a previous query's rows visible.
            handleSearch(val);
            onInputChange?.(val);
          }}
          placeholder={placeholder ? placeholder : `Select ${label}`}
        />
        {iconSlot}
      </div>
    </Form.Item>
  );
};

export default LocationAutoCompleteShipment;
